import { IHyperliquidPosition } from "../../interfaces/hyperliquid";
import { IAggregatedPosition } from "../../interfaces/positions";
import { convertToAevoAssetName } from "../assetNameMappings";

/**
 * Extracts the asset name from Hyperliquid coin format.
 * Hyperliquid coins can have prefixes like "xyz:" (e.g., "xyz:TSLA" -> "TSLA")
 */
const extractAssetFromCoin = (coin: string): string => {
    // Remove any prefix like "xyz:" or similar
    if (coin.includes(":")) {
        return coin.split(":").pop() || coin;
    }
    return coin;
};

/**
 * Standardizes Hyperliquid positions to the IAggregatedPosition format.
 * Hyperliquid uses coin names like "xyz:TSLA" for perpetuals.
 */
export const standardizeHyperliquidPositions = (
  positions: IHyperliquidPosition[]
): IAggregatedPosition[] => {
  if (!positions || !Array.isArray(positions)) {
    return [];
  }

  const standardizedPositions: Record<string, IAggregatedPosition> = {};

  positions.forEach((pos) => {
    const size = Number(pos.size);

    if (Number.isNaN(size) || size === 0) {
      return;
    }

    const sizeAbs = Number(pos.sizeAbs || Math.abs(size));
    const entryPrice = pos.entryPrice ? Number(pos.entryPrice) : 0;
    let markPrice = entryPrice;
    if (pos.markPrice) {
      markPrice = Number(pos.markPrice);
    } else if (sizeAbs > 0) {
      markPrice = Number(pos.positionValue) / sizeAbs;
    }
    const unrealizedPnl = Number(pos.unrealizedPnl || 0);
    const asset = convertToAevoAssetName(
      extractAssetFromCoin(pos.coin),
      "hyperliquid"
    );
    const instrumentName = `${asset}-PERPETUAL`;

    if (!standardizedPositions[instrumentName]) {
      standardizedPositions[instrumentName] = {
        instrument_name: instrumentName,
        isPerpetual: true,
        asset,
        delta: 0,
        gamma: 0,
        vega: 0,
        theta: 0,
        contracts: 0,
        pnl: 0,
        side: "-",
        source: "Hyperliquid",
        hyperliquidDelta: 0,
        hyperliquidMarkPrice: 0,
      };
    }

    standardizedPositions[instrumentName].delta += size;
    standardizedPositions[instrumentName].contracts += size;
    standardizedPositions[instrumentName].pnl += unrealizedPnl;
    standardizedPositions[instrumentName].side =
      standardizedPositions[instrumentName].contracts > 0 ? "buy" : "sell";
    standardizedPositions[instrumentName].hyperliquidDelta =
      (standardizedPositions[instrumentName].hyperliquidDelta || 0) + size;

    if (markPrice) {
      standardizedPositions[instrumentName].hyperliquidMarkPrice = markPrice;
    }
  });

  return Object.values(standardizedPositions);
};
